/**
 * Enemy type definitions and stat-scaling helpers.
 * Wave N uses `getEnemyHp(baseHp, N)` — a smoother ramp:
 * +4% early waves, +5% mid waves, +6% late waves.
 */

export interface EnemyConfig {
  id: string;
  name: string;
  baseHp: number;
  speed: number;
  /** Damage dealt to the player on contact */
  damage: number;
  /** Circle radius in pixels */
  size: number;
  /** Phaser hex colour (e.g. 0xff6600) */
  color: number;
  /** XP awarded when the enemy is defeated — used by future reward systems. */
  xpReward: number;
}

export const ENEMY_TYPES: EnemyConfig[] = [
  {
    id: 'grunt',
    name: 'Grunt',
    baseHp: 20,
    speed: 60,
    damage: 10,
    size: 16,
    color: 0xff6600,
    xpReward: 5,
  },
  {
    id: 'speeder',
    name: 'Speeder',
    baseHp: 10,
    speed: 100,
    damage: 5,
    size: 12,
    color: 0xffff00,
    xpReward: 3,
  },
  {
    id: 'tank',
    name: 'Tank',
    baseHp: 50,
    speed: 40,
    damage: 20,
    size: 24,
    color: 0x8800ff,
    xpReward: 10,
  },
  {
    id: 'ghost',
    name: 'Ghost',
    baseHp: 15,
    speed: 75,
    damage: 8,
    size: 14,
    color: 0xccaaff,
    xpReward: 4,
  },
];

export type BossType = 'forest' | 'cave' | 'graveyard' | 'castle';

/**
 * Determines boss type from the wave number.
 * Cycles through: forest (wave 5) → cave (10) → graveyard (15) → castle (20+, repeating).
 */
export function getBossType(wave: number): BossType {
  const index = Math.floor((wave - 1) / 5) % 4;
  const types: BossType[] = ['forest', 'cave', 'graveyard', 'castle'];
  return types[index];
}

/** Boss stat multipliers applied on top of the base wave enemy. */
export const BOSS_CONFIG = {
  /** Issue 2.2: Boss HP = 10× normal enemy HP. */
  hpMultiplier: 10,
  /** Issue 2.2: Boss is 1.5× the size of a normal enemy. */
  sizeMultiplier: 1.5,
  speedMultiplier: 0.8,
  damageMultiplier: 2,
  /** Gold rewarded to the player on defeat. */
  goldReward: 100,
  /** XP rewarded to the player on defeat. */
  xpReward: 50,
  color: 0xff0000,
  glowColor: 0xff4444,
} as const;

/** Per-boss-type visual and display config. */
export const BOSS_TYPE_CONFIG: Record<BossType, { name: string; color: number; glowColor: number }> = {
  forest:    { name: 'Forest Guardian',  color: 0x228b22, glowColor: 0x44ff44 },
  cave:      { name: 'Cave Lurker',      color: 0x555555, glowColor: 0x9966ff },
  graveyard: { name: 'Graveyard Lich',   color: 0x8888ff, glowColor: 0xaaaaff },
  castle:    { name: 'Castle Overlord',  color: 0xcc0000, glowColor: 0xff4444 },
};

/**
 * Returns HP for an enemy in wave `wave`.
 * Wave ramp:
 * - Waves 1–10: +4% per wave
 * - Waves 11–20: +5% per wave
 * - Waves 21+: +6% per wave
 */
export function getEnemyHp(baseHp: number, wave: number): number {
  const wavesPassed = Math.max(0, wave - 1);
  const early = Math.min(wavesPassed, 10);
  const mid = Math.min(Math.max(wavesPassed - 10, 0), 10);
  const late = Math.max(wavesPassed - 20, 0);

  const hp = baseHp * Math.pow(1.04, early) * Math.pow(1.05, mid) * Math.pow(1.06, late);
  return Math.round(hp);
}
